/** Κοινά των γραφημάτων: κλίμακες, «στρογγυλά» μέγιστα, διαδρομές SVG, μέτρηση κειμένου. */

/** Ένα δεκαδικό: λιγότερα bytes στα path, καμία ορατή διαφορά. */
export const f1 = (v: number): number => Math.round(v * 10) / 10;

/** Το επόμενο «στρογγυλό» άνω όριο: 1 · 2 · 2,5 · 5 · 10 × 10ⁿ. Μη θετικό → 1. */
export function niceMax(v: number): number {
  if (!(v > 0) || !Number.isFinite(v)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
}

/** Γραμμική κλίμακα [d0,d1] → [r0,r1] (σταθερό πεδίο → r0). */
export const linear = (d0: number, d1: number, r0: number, r1: number) => (v: number): number =>
  d1 === d0 ? r0 : r0 + ((v - d0) * (r1 - r0)) / (d1 - d0);

/** Στήλη: στρογγυλό το άκρο των δεδομένων (πάνω), ίσια η βάση. Ύψος ~0 → καμία διαδρομή. */
export function colPath(x: number, y: number, w: number, h: number, r = 4): string {
  if (!(h > 0.5) || !(w > 0)) return '';
  const rr = Math.min(r, w / 2, h);
  return `M${f1(x)} ${f1(y + h)}V${f1(y + rr)}A${rr} ${rr} 0 0 1 ${f1(x + rr)} ${f1(y)}H${f1(x + w - rr)}A${rr} ${rr} 0 0 1 ${f1(x + w)} ${f1(y + rr)}V${f1(y + h)}Z`;
}

/** Οριζόντια μπάρα από το x0: ίσια αριστερά, στρογγυλή δεξιά. */
export function hbarPath(x0: number, y: number, len: number, h: number): string {
  const L = Math.max(2, len);
  const r = Math.min(h / 2, L / 2);
  return `M${f1(x0)} ${f1(y)}H${f1(x0 + L - r)}A${f1(r)} ${f1(r)} 0 0 1 ${f1(x0 + L)} ${f1(y + r)}V${f1(y + h - r)}A${f1(r)} ${f1(r)} 0 0 1 ${f1(x0 + L - r)} ${f1(y + h)}H${f1(x0)}Z`;
}

/** Γραμμή από σημεία [x, y]. */
export const linePath = (pts: Array<[number, number]>): string =>
  pts.map((p, i) => `${i ? 'L' : 'M'}${f1(p[0])} ${f1(p[1])}`).join('');

let ctx: CanvasRenderingContext2D | null | undefined;
const FONT = '"Segoe UI", system-ui, sans-serif';

/** Πλάτος κειμένου σε px (canvas· χωρίς canvas, π.χ. jsdom, εκτίμηση ~0,56 em ανά χαρακτήρα). */
export function textWidth(text: string, size = 12, weight = 600): number {
  if (ctx === undefined) {
    try {
      ctx = typeof document !== 'undefined' && typeof navigator !== 'undefined' && !/jsdom/i.test(navigator.userAgent)
        ? document.createElement('canvas').getContext('2d')
        : null;
    } catch {
      ctx = null;
    }
  }
  if (ctx) {
    ctx.font = `${weight} ${size}px ${FONT}`;
    return ctx.measureText(text).width;
  }
  return text.length * size * 0.56;
}
